import { LoadChildrenCallback, RouterModule, Routes } from '@angular/router';
import { MainLayoutComponent } from './main-layout/main-layout.component';
import { NgModule } from '@angular/core';
import { CognitoUserLoggedIn } from '@auth/guards/cognito-user-logged-in.service';
import { NavigationKey } from '@generated/graphql';
import { AuthGuard } from '@auth/guards/auth.guard';
import { SuperadminGuard } from '@app/auth/guards/superadmin.guard';
import { PipelineAuthGuard } from '@app/pages/pipeline/guards/pipeline-auth.guard';
import {
  getPipelineModule,
  loadSalesPipelineModule,
  loadTicketingPipelineModule,
} from '@app/pages/lazy-modules';
import { SearchFlightsRedirectGuard } from '@app/shared/guards/search-flights-redirect.guard';
import { InsightsRedirectGuard } from '@app/shared/guards/insights-redirect.guard';
import { MonthlyStatsPageGuard } from '@app/shared/guards/monthly-stats-page.guard';
import { NavigationGuard } from '@app/shared/guards/navigation.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: 'login',
        loadChildren: () => import('../pages/login/login.module').then((m) => m.LoginModule),
      },
      {
        // Public on purpose: users open it when the app is slow or unreachable.
        path: 'network',
        loadChildren: () =>
          import('../pages/network-diagnostics/network-diagnostics.module').then(
            (m) => m.NetworkDiagnosticsModule,
          ),
      },
      {
        path: 'clients',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Clients,
        },
        loadChildren: () => import('../pages/clients/clients.module').then((m) => m.ClientsModule),
      },
      {
        path: 'miles-inventory',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.MilesInventory,
        },
        loadChildren: () =>
          import('../pages/miles-inventory/miles-inventory.module').then(
            (m) => m.MilesInventoryModule,
          ),
      },
      {
        path: 'users',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Users,
        },
        loadChildren: () => import('../pages/users/users.module').then((m) => m.UsersModule),
      },
      {
        path: 'checklist-tasks',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.ChecklistTasks,
        },
        loadChildren: () =>
          import('../pages/checklist-tasks/checklist-tasks.module').then(
            (m) => m.ChecklistTasksModule,
          ),
      },
      {
        path: 'checklist-templates',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.ChecklistTemplates,
        },
        loadChildren: () =>
          import('../pages/checklist-templates/checklist-templates.module').then(
            (m) => m.ChecklistTemplatesModule,
          ),
      },
      {
        path: 'governance-systems',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.GovernanceSystems,
        },
        loadChildren: () =>
          import('../pages/governance-systems/governance-systems.module').then(
            (m) => m.GovernanceSystemsModule,
          ),
      },
      {
        path: 'governance-matrix',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.GovernanceMatrix,
        },
        loadChildren: () =>
          import('../pages/governance-matrix/governance-matrix.module').then(
            (m) => m.GovernanceMatrixModule,
          ),
      },
      {
        path: 'governance-audit-log',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.GovernanceAuditLog,
        },
        loadChildren: () =>
          import('../pages/governance-audit-log/governance-audit-log.module').then(
            (m) => m.GovernanceAuditLogModule,
          ),
      },
      {
        path: 'identity-contracts',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.IdentityContracts,
        },
        loadChildren: () =>
          import('../pages/identity-contracts/identity-contracts.module').then(
            (m) => m.IdentityContractsModule,
          ),
      },
      {
        path: 'identity-eula-versions',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.IdentityEulaVersions,
        },
        loadChildren: () =>
          import('../pages/identity-eula-versions/identity-eula-versions.module').then(
            (m) => m.IdentityEulaVersionsModule,
          ),
      },
      {
        path: 'identity-de-anon-gaps',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.IdentityDeAnonGaps,
        },
        loadChildren: () =>
          import('../pages/identity-de-anon-gaps/identity-de-anon-gaps.module').then(
            (m) => m.IdentityDeAnonGapsModule,
          ),
      },
      {
        path: 'identity-audit-log',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.IdentityAuditLog,
        },
        loadChildren: () =>
          import('../pages/identity-audit-log/identity-audit-log.module').then(
            (m) => m.IdentityAuditLogModule,
          ),
      },
      {
        path: 'sales-organization',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.SalesOrganization,
        },
        loadChildren: () =>
          import('../pages/sales-organization/sales-organization.module').then(
            (m) => m.SalesOrganizationModule,
          ),
      },
      {
        path: 'permission-matrix',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.PermissionMatrix,
        },
        loadChildren: () =>
          import('../pages/permission-matrix/permission-matrix.module').then(
            (m) => m.PermissionMatrixModule,
          ),
      },
      {
        path: 'my-queue',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.MyQueue,
        },
        loadChildren: () =>
          import('../pages/my-queue/my-queue.module').then((m) => m.MyQueueModule),
      },
      {
        path: 'upcoming-flights',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.UpcomingFlights,
        },
        loadChildren: () =>
          import('../pages/upcoming-flights/upcoming-flights.module').then(
            (m) => m.UpcomingFlightsModule,
          ),
      },
      {
        path: 'orders',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Orders,
        },
        loadChildren: () => import('../pages/orders/orders.module').then((m) => m.OrdersV2Module),
      },
      {
        path: 'costs',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Costs,
        },
        loadChildren: () => import('../pages/costs/costs.module').then((m) => m.CostsModule),
      },
      {
        path: 'financial-events',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.FinancialEvents,
        },
        loadChildren: () =>
          import('../pages/financial-events/financial-events.module').then(
            (m) => m.FinancialEventsModule,
          ),
      },
      {
        path: 'invoices',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Invoices,
        },
        loadChildren: () =>
          import('../pages/invoices/invoices.module').then((m) => m.InvoicesModule),
      },
      {
        path: 'profile',
        canActivate: [CognitoUserLoggedIn, AuthGuard, SuperadminGuard],
        loadChildren: () => import('../pages/profile/profile.module').then((m) => m.ProfileModule),
      },
      {
        path: 'team-dashboard',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard, MonthlyStatsPageGuard],
        data: {
          navKey: NavigationKey.TeamDashboard,
        },
        loadChildren: () =>
          import('../pages/team-dashboard/team-dashboard.module').then(
            (m) => m.TeamDashboardModule,
          ),
      },
      {
        // EC-758: each board on its own URL, guarded by the Pipeline section key and its
        // shell key together; registered before bare `pipeline` so they match first.
        path: 'pipeline/sales',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKeys: [NavigationKey.Pipeline, NavigationKey.PipelineSales],
        },
        loadChildren: loadSalesPipelineModule,
      },
      {
        path: 'pipeline/ticketing',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKeys: [NavigationKey.Pipeline, NavigationKey.PipelineTicketing],
        },
        loadChildren: loadTicketingPipelineModule,
      },
      {
        path: 'pipeline',
        canActivate: [CognitoUserLoggedIn, PipelineAuthGuard, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Pipeline,
        },
        loadChildren: getPipelineModule() as LoadChildrenCallback,
      },
      {
        path: 'inprogress',
        canActivate: [CognitoUserLoggedIn, NavigationGuard],
        data: {
          navKey: NavigationKey.InProgress,
        },
        loadChildren: () =>
          import('../pages/in-progress/in-progress.module').then((m) => m.InProgressModule),
      },
      {
        path: 'airline-classes',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.AirlineClasses,
        },
        loadChildren: () =>
          import('../pages/airline-classes/airline-classes.module').then(
            (m) => m.AirlineClassesModule,
          ),
      },
      {
        path: 'miles-programs',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.MilesPrograms,
        },
        loadChildren: () =>
          import('../pages/miles-programs/miles-programs.module').then(
            (m) => m.MilesProgramsModule,
          ),
      },
      {
        path: 'promo-campaigns',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.PromoCampaigns,
        },
        loadChildren: () =>
          import('../pages/promo-campaigns/promo-campaigns.module').then(
            (m) => m.PromoCampaignsModule,
          ),
      },
      {
        path: 'insights',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard, InsightsRedirectGuard],
        data: {
          navKey: NavigationKey.Insights,
        },
        loadChildren: () =>
          import('../pages/insights/insights.module').then((m) => m.InsightsModule),
      },
      {
        path: 'support-requests',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.SupportRequests,
        },
        loadChildren: () =>
          import('../pages/support-requests/support-requests.module').then(
            (m) => m.SupportRequestsModule,
          ),
      },
      {
        path: 'bookings',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Bookings,
        },
        loadChildren: () =>
          import('../pages/bookings/bookings.module').then((m) => m.BookingsModule),
      },
      {
        path: 'templates',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Templates,
        },
        loadChildren: () =>
          import('../pages/templates/templates.module').then((m) => m.TemplatesModule),
      },
      {
        path: 'status-email-routing',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Templates,
        },
        loadChildren: () =>
          import('../pages/status-email-routing/status-email-routing.module').then(
            (m) => m.StatusEmailRoutingModule,
          ),
      },
      {
        path: 'slack-channels',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Templates,
        },
        loadChildren: () =>
          import('../pages/slack-channels/slack-channels.module').then(
            (m) => m.SlackChannelsModule,
          ),
      },
      {
        // Agency parameter admin page: the 15 non-clock tunables of `rules §13`. Administrators
        // only — the reads and the write are gated on `user.update` alone, and the route has a
        // key of its own for exactly that capability. Not `NavigationKey.Users`, which also opens
        // for the sales leads, IT_SECURITY and CEO.
        path: 'agency-parameters',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.AgencyParameters,
        },
        loadChildren: () =>
          import('../pages/agency-parameters/agency-parameters.module').then(
            (m) => m.AgencyParametersModule,
          ),
      },
      {
        // Agency shifts admin page, in the Admin menu. `NavigationKey` is a backend enum, so a
        // new key would need a backend change — this piggybacks `Users`, the way
        // `slack-channels` piggybacks `Templates`.
        path: 'shifts',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Users,
        },
        loadChildren: () => import('../pages/shifts/shifts.module').then((m) => m.ShiftsModule),
      },
      {
        path: 'payments',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Payments,
        },
        loadChildren: () =>
          import('../pages/payments/payments.module').then((m) => m.PaymentsModule),
      },
      {
        path: 'leads',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Leads,
        },
        loadChildren: () => import('../pages/leads/leads.module').then((m) => m.LeadsModule),
      },
      {
        path: 'flight-requests',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.FlightRequests,
        },
        loadChildren: () =>
          import('../pages/flight-requests/flight-requests.module').then(
            (m) => m.FlightRequestsModule,
          ),
      },
      {
        path: 'search-flights',
        canActivate: [CognitoUserLoggedIn, AuthGuard, SearchFlightsRedirectGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.SearchFlights,
        },
        loadChildren: () => import('../pages/search/search.module').then((m) => m.SearchModule),
      },
      {
        path: 'complex-search-flights',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          isComplexSearchFlights: true,
          navKey: NavigationKey.SearchFlights,
        },
        loadChildren: () => import('../pages/search/search.module').then((m) => m.SearchModule),
      },
      {
        path: 'trips',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Trips,
        },
        loadChildren: () => import('../pages/trips/trips.module').then((m) => m.TripsModule),
      },
      {
        path: 'change-requests',
        canActivate: [CognitoUserLoggedIn, AuthGuard],
        loadChildren: () =>
          import('../pages/change-requests/change-requests.module').then(
            (m) => m.ChangeRequestsModule,
          ),
      },
      {
        path: 'miles-transfers',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.MilesTransfers,
        },
        loadChildren: () =>
          import('../pages/miles-transfers/miles-transfers.module').then(
            (m) => m.MilesTransfersModule,
          ),
      },
      {
        path: 'pnrs',
        canActivate: [CognitoUserLoggedIn, AuthGuard, NavigationGuard],
        data: {
          navKey: NavigationKey.Pnrs,
        },
        loadChildren: () => import('../pages/pnrs/pnrs.module').then((m) => m.PnrsModule),
      },
      {
        // Temporary Looker Studio embed test page (TIC-6915)
        path: 'looker-test',
        canActivate: [CognitoUserLoggedIn],
        loadChildren: () =>
          import('../pages/looker-test/looker-test.module').then((m) => m.LookerTestModule),
      },
      // todo depricated redirects
      {
        path: 'root-orders',
        redirectTo: 'orders',
      },
      {
        path: 'sales/leads',
        redirectTo: 'leads',
      },
      {
        path: 'sales/flight-requests',
        redirectTo: 'flight-requests',
      },
      {
        path: 'sales/clients',
        redirectTo: 'clients',
      },
      {
        path: 'sales/upcoming-flights',
        redirectTo: 'upcoming-flights',
      },
      {
        path: 'sales/support-requests',
        redirectTo: 'support-requests',
      },
      {
        path: 'searcher/my-queue',
        redirectTo: 'my-queue',
      },
      {
        path: 'accounting/bookings',
        redirectTo: 'bookings',
      },
      {
        path: 'accounting/payments',
        redirectTo: 'payments',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/login',
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class LayoutRoutingModule {}
